import type { PartnerCityId } from '../../content';
import type { PlaceDetails } from './types';

const FOOD_HOURS = {
  mon: { open: true, from: '08:00', to: '22:00' },
  tue: { open: true, from: '08:00', to: '22:00' },
  wed: { open: true, from: '08:00', to: '22:00' },
  thu: { open: true, from: '08:00', to: '22:00' },
  fri: { open: true, from: '08:00', to: '23:00' },
  sat: { open: true, from: '09:00', to: '23:00' },
  sun: { open: true, from: '09:00', to: '21:00' },
} as const;

const WELLNESS_HOURS = {
  mon: { open: true, from: '07:00', to: '21:00' },
  tue: { open: true, from: '07:00', to: '21:00' },
  wed: { open: true, from: '07:00', to: '21:00' },
  thu: { open: true, from: '07:00', to: '21:00' },
  fri: { open: true, from: '07:00', to: '20:00' },
  sat: { open: true, from: '09:00', to: '14:00' },
  sun: { open: false, from: '09:00', to: '14:00' },
} as const;

function mockPhotoUrls(placeId: string, count: number): string[] {
  return Array.from({ length: count }, (_, index) =>
    `https://picsum.photos/seed/${placeId}-${index + 1}/800/600`,
  );
}

/** Replace with real Places API data once backend is wired. */
export const MOCK_PLACES: PlaceDetails[] = [
  {
    placeId: 'ChIJpalms_cafe_wynwood_miami',
    name: 'Palms & Coffee Wynwood',
    formattedAddress: '2450 NW 2nd Ave, Miami, FL 33127',
    cityId: 'miami',
    category: 'food',
    description:
      'Fictional Wynwood café with outdoor seating, weekend brunch and specialty coffee.',
    website: 'https://palms-coffee.example',
    hours: { ...FOOD_HOURS },
    photoUrls: mockPhotoUrls('palms_coffee_wynwood', 4),
    mapsUrl: 'https://www.google.com/maps/place/Palms+and+Coffee+Wynwood/@25.8004,-80.1995,17z/data=!4m6!3m5!1sChIJpalms_cafe_wynwood_miami',
  },
  {
    placeId: 'ChIJflow_pilates_brickell_miami',
    name: 'Flow Pilates Brickell',
    formattedAddress: '1101 Brickell Ave, Miami, FL 33131',
    cityId: 'miami',
    category: 'wellness',
    description:
      'Fictional Brickell wellness studio offering reformer pilates and small-group classes.',
    website: 'https://flow-pilates.example',
    hours: { ...WELLNESS_HOURS },
    photoUrls: mockPhotoUrls('flow_pilates_brickell', 4),
    mapsUrl: 'https://www.google.com/maps/place/Flow+Pilates+Brickell/@25.7622,-80.1910,17z/data=!4m6!3m5!1sChIJflow_pilates_brickell_miami',
  },
  {
    placeId: 'ChIJcafe_sol_barcelona',
    name: 'Café Sol',
    formattedAddress: 'Carrer de Verdi 12, 08012 Barcelona',
    cityId: 'barcelona',
    category: 'food',
    description:
      'Café de barrio en Gràcia con terraza, brunch de fin de semana y café de especialidad.',
    website: 'https://cafesol.example',
    hours: { ...FOOD_HOURS },
    photoUrls: mockPhotoUrls('cafe_sol', 4),
    mapsUrl: 'https://www.google.com/maps/place/Café+Sol/@41.4036,2.1574,17z/data=!4m6!3m5!1sChIJcafe_sol_barcelona',
  },
  {
    placeId: 'ChIJyoga_gracia_barcelona',
    name: 'Yoga Gràcia Studio',
    formattedAddress: 'Carrer de Torrijos 45, 08012 Barcelona',
    cityId: 'barcelona',
    category: 'wellness',
    description: 'Estudio de yoga y pilates reformer en el corazón de Gràcia. Clases para todos los niveles.',
    website: 'https://yogragracia.example',
    hours: { ...WELLNESS_HOURS },
    photoUrls: mockPhotoUrls('yoga_gracia', 3),
    mapsUrl: 'https://www.google.com/maps/place/Yoga+Gràcia+Studio/@41.4021,2.1589,17z/data=!4m6!3m5!1sChIJyoga_gracia_barcelona',
  },
  {
    placeId: 'ChIJla_latina_madrid',
    name: 'Restaurante La Latina',
    formattedAddress: 'Calle de la Cava Baja 35, 28005 Madrid',
    cityId: 'madrid',
    category: 'food',
    description: 'Taberna tradicional en La Latina. Tapas caseras y vinos de la región.',
    website: 'https://lalatina.example',
    hours: { ...FOOD_HOURS, sun: { open: false, from: '12:00', to: '23:00' } },
    photoUrls: mockPhotoUrls('la_latina', 5),
    mapsUrl: 'https://www.google.com/maps/place/Restaurante+La+Latina/@40.4123,-3.7089,17z/data=!4m6!3m5!1sChIJla_latina_madrid',
  },
  {
    placeId: 'ChIJmetro_fitness_madrid',
    name: 'Metro Fitness Chamberí',
    formattedAddress: 'Calle de Bravo Murillo 198, 28020 Madrid',
    cityId: 'madrid',
    category: 'wellness',
    description: 'Gimnasio con sala de musculación, clases dirigidas y entrenamiento personal.',
    website: 'https://metrofitness.example',
    hours: { ...WELLNESS_HOURS, sat: { open: true, from: '08:00', to: '18:00' } },
    photoUrls: mockPhotoUrls('metro_fitness', 3),
    mapsUrl: 'https://www.google.com/maps/place/Metro+Fitness+Chamberí/@40.4478,-3.7021,17z/data=!4m6!3m5!1sChIJmetro_fitness_madrid',
  },
  {
    placeId: 'ChIJlocal_ambiguo_barcelona',
    name: 'Local Comercial Passeig de Gràcia',
    formattedAddress: 'Passeig de Gràcia 88, 08008 Barcelona',
    cityId: 'barcelona',
    category: null,
    description: 'Espacio comercial en zona céntrica. Consultar actividad con el propietario.',
    website: '',
    hours: { ...FOOD_HOURS },
    photoUrls: mockPhotoUrls('local_ambiguo', 2),
    mapsUrl: 'https://www.google.com/maps/place/Local+Comercial/@41.3948,2.1634,17z/data=!4m6!3m5!1sChIJlocal_ambiguo_barcelona',
  },
  {
    placeId: 'ChIJestudio_malasana_madrid',
    name: 'Estudio Creativo Malasaña',
    formattedAddress: 'Calle del Pez 12, 28004 Madrid',
    cityId: 'madrid',
    category: null,
    description: 'Espacio polivalente en Malasaña. Actividades variadas según programación.',
    photoUrls: mockPhotoUrls('estudio_malasana', 2),
    mapsUrl: 'https://www.google.com/maps/place/Estudio+Creativo+Malasaña/@40.4261,-3.7034,17z/data=!4m6!3m5!1sChIJestudio_malasana_madrid',
  },
];

export const MOCK_PLACE_BY_ID = new Map(MOCK_PLACES.map((place) => [place.placeId, place]));

/** Simulated cid → placeId map for pasted Google Maps links. */
export const MOCK_CID_TO_PLACE_ID: Record<string, string> = {
  '30524501101': 'ChIJpalms_cafe_wynwood_miami',
  '30511011101': 'ChIJflow_pilates_brickell_miami',
  '1847263940182': 'ChIJcafe_sol_barcelona',
  '9283746501928': 'ChIJyoga_gracia_barcelona',
};

export function filterMockSuggestions(query: string, cityId?: PartnerCityId) {
  const normalized = query.trim().toLowerCase();
  if (normalized.length < 2) return [];

  return MOCK_PLACES.filter((place) => {
    const matchesQuery =
      place.name.toLowerCase().includes(normalized) ||
      place.formattedAddress.toLowerCase().includes(normalized);
    const matchesCity = !cityId || place.cityId === cityId;
    return matchesQuery && matchesCity;
  }).map((place) => ({
    placeId: place.placeId,
    primaryText: place.name,
    secondaryText: place.formattedAddress,
  }));
}
